import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';
import { normalizeTaskReflectionPayload } from '../../utils/taskReflectionNormalize';

/**
 * Task Reflection Agent Service
 * Generates AI-powered reflections for completed tasks
 * Similar to journal reflections but focused on individual task completion
 */
export const TaskReflectionAgentService = {
  /**
   * Generate AI reflection for a completed task
   * @param {Object} taskData - Completed task data
   * @returns {Promise<Object>} - AI-generated reflection with insights
   */
  async generateTaskReflection(taskData) {
    const payload = {
      userId: taskData.userId,
      taskId: taskData.taskId,

      // Task details
      taskInfo: {
        title: taskData.what || taskData.title,
        description: taskData.description || taskData.taskDescription || '',
        priority: taskData.priority || 'medium',
        urgencyLevel: taskData.urgencyLevel || 'medium',
        learningGoal: taskData.learningGoal || '',
        why: taskData.why || '',
        how: taskData.how || '',
      },

      // Time tracking
      timeData: {
        estimatedMinutes: taskData.estimatedMinutes || null,
        timeSpent: taskData.timeSpent || 0, // in seconds
        scheduledTime: taskData.scheduledTime || taskData.when,
        startedAt: taskData.startedAt,
        completedAt: taskData.completedAt || new Date().toISOString(),
      },

      // Completion details
      completionData: {
        completed: taskData.completed || false,
        taskOutcome: taskData.taskOutcome || 'completed',
        completionPercentage: taskData.completionPercentage || 100,
        productivityRating: taskData.productivityRating || null,
        focusQuality: taskData.focusQuality || null,
      },

      // User reflection responses (if any)
      userResponses: taskData.userReflectionResponses || {},

      // Context
      context: {
        previousReflections: taskData.previousReflections || [],
        relatedTasks: taskData.relatedTasks || [],
        courseContext: taskData.courseContext || null,
        projectContext: taskData.projectContext || null,
      },
    };

    const response = await fetch(`${API_BASE_URL}/reflection/generate-task-reflection`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const normalized = normalizeTaskReflectionPayload(data);
    return {
      aiReflection: normalized.aiReflection,
      insights: data.insights || normalized.insights || [],
      patterns: data.patterns || normalized.patterns || [],
      recommendations: data.recommendations || normalized.recommendations || [],
      depthScore: data.depthScore || data.reflectionDepthScore || 5,
      emotionalTone: data.emotionalTone || normalized.emotionalTone || 'neutral',
      keyLearnings: data.keyLearnings || [],
      challengesFaced: data.challengesFaced || [],
      solutionsApplied: data.solutionsApplied || [],
      skillsPracticed: data.skillsPracticed || [],
    };
  },

  /**
   * Generate reflection prompts for task completion
   * @param {Object} taskData - Task data
   * @returns {Promise<Array>} - Reflection questions
   */
  async generateTaskReflectionPrompts(taskData) {
    const payload = {
      taskId: taskData.taskId,
      taskTitle: taskData.what || taskData.title,
      taskType: taskData.type || 'general',
      priority: taskData.priority || 'medium',
      learningGoal: taskData.learningGoal || '',
      timeSpent: taskData.timeSpent || 0,
      estimatedTime: taskData.estimatedMinutes ? taskData.estimatedMinutes * 60 : null,
    };

    const response = await fetch(`${API_BASE_URL}/reflection/task-prompts`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.prompts || [];
  },

  /**
   * Analyze user's task reflection responses
   * @param {Object} reflectionData - User's reflection responses
   * @returns {Promise<Object>} - AI analysis
   */
  async analyzeTaskReflection(reflectionData) {
    const payload = {
      taskId: reflectionData.taskId,
      taskTitle: reflectionData.taskTitle,
      userResponses: reflectionData.userResponses,
      taskData: {
        priority: reflectionData.priority,
        timeSpent: reflectionData.timeSpent,
        estimatedTime: reflectionData.estimatedTime,
        completed: reflectionData.completed,
      },
    };

    const response = await fetch(`${API_BASE_URL}/reflection/analyze-task`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      insights: data.insights || [],
      strengths: data.strengths || [],
      areasForGrowth: data.areasForGrowth || [],
      suggestions: data.suggestions || [],
      depthScore: data.depthScore || 5,
    };
  },

  /**
   * Get productivity insights from multiple task completions
   * @param {Object} tasksData - Multiple completed tasks
   * @returns {Promise<Object>} - Productivity insights
   */
  async getProductivityInsights(tasksData) {
    const payload = {
      userId: tasksData.userId,
      timeframe: tasksData.timeframe || 'week',
      completedTasks: tasksData.completedTasks || [],
      totalTimeSpent: tasksData.totalTimeSpent || 0,
      averageProductivity: tasksData.averageProductivity || null,
    };

    const response = await fetch(`${API_BASE_URL}/reflection/productivity-insights`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      patterns: data.patterns || [],
      strengths: data.strengths || [],
      improvements: data.improvements || [],
      recommendations: data.recommendations || [],
      timeManagementScore: data.timeManagementScore || 5,
      focusScore: data.focusScore || 5,
    };
  },

  /**
   * Generate a quick reflection using the chat endpoint
   * Useful for simple task reflections without full API support
   * @param {Object} taskData - Task data
   * @returns {Promise<Object>} - AI-generated reflection
   */
  async generateQuickReflection(taskData) {
    const timeSpentMinutes = Math.round((taskData.timeSpent || 0) / 60);
    const estimatedMinutes = taskData.estimatedMinutes || 0;
    const timeDiff = timeSpentMinutes - estimatedMinutes;
    const timeComparison =
      estimatedMinutes > 0
        ? timeDiff > 0
          ? `${timeDiff} minutes over estimate`
          : timeDiff < 0
            ? `${Math.abs(timeDiff)} minutes under estimate`
            : 'exactly as estimated'
        : 'no estimate provided';

    const contextMessage = `Please generate a thoughtful reflection for this completed task:

Task: ${taskData.what || taskData.title}
Priority: ${taskData.priority || 'medium'}
Time Spent: ${timeSpentMinutes} minutes (${timeComparison})
Completion: ${taskData.completionPercentage || 100}%
Outcome: ${taskData.taskOutcome || 'completed'}

${
  taskData.userReflectionResponses
    ? `User's Reflection Responses:
${Object.entries(taskData.userReflectionResponses)
  .map(([key, value]) => `${key}: ${value}`)
  .join('\n')}`
    : ''
}

Please provide:
1. A brief reflection on what was accomplished
2. Key insights or learnings
3. Patterns observed (time management, focus, etc.)
4. Recommendations for future similar tasks
5. Skills practiced or developed

Format the response as a cohesive reflection that captures the task completion experience.`;

    const payload = {
      agentId: 'reflection',
      persona: taskData.persona || null,
      message: contextMessage,
      userId: taskData.userId || 'unknown-user',
      userName: 'Student',
      userProfile: {},
      chatHistory: [],
      reflectionContext: {
        taskId: taskData.taskId,
        taskData: taskData,
      },
    };

    const response = await fetch(`${API_BASE_URL}/reflection/chat`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    const normalized = normalizeTaskReflectionPayload(data);
    const followUpQuestions =
      data.followUpQuestions ||
      (typeof data.response === 'string' && data.response.trim().startsWith('{')
        ? (() => {
            try {
              const parsed = JSON.parse(data.response);
              return parsed.followUpQuestions || [];
            } catch {
              return [];
            }
          })()
        : []);

    return {
      aiReflection: normalized.aiReflection,
      insights:
        data.insights || normalized.insights || this._extractInsights(normalized.aiReflection),
      patterns: data.patterns || [],
      recommendations:
        data.recommendations ||
        normalized.recommendations ||
        this._extractRecommendations(normalized.aiReflection),
      followUpQuestions: followUpQuestions,
      depthScore: 7, // Default score for quick reflections
      emotionalTone: data.emotionalTone || 'neutral',
      keyLearnings: data.keyLearnings || [],
      skillsPracticed: data.skillsPracticed || [],
    };
  },

  /**
   * Helper: Extract insights from reflection text
   * @private
   */
  _extractInsights(text) {
    const insights = [];
    const insightPatterns = [
      /insight[s]?:?\s*(.+?)(?:\n|$)/gi,
      /learned:?\s*(.+?)(?:\n|$)/gi,
      /realized:?\s*(.+?)(?:\n|$)/gi,
    ];

    insightPatterns.forEach((pattern) => {
      const matches = text.matchAll(pattern);
      for (const match of matches) {
        if (match[1]) {
          insights.push(match[1].trim());
        }
      }
    });

    return insights.slice(0, 5); // Limit to 5 insights
  },

  /**
   * Helper: Extract recommendations from reflection text
   * @private
   */
  _extractRecommendations(text) {
    const recommendations = [];
    const recPatterns = [
      /recommend[s|ation]*:?\s*(.+?)(?:\n|$)/gi,
      /suggest[s|ion]*:?\s*(.+?)(?:\n|$)/gi,
      /next time:?\s*(.+?)(?:\n|$)/gi,
    ];

    recPatterns.forEach((pattern) => {
      const matches = text.matchAll(pattern);
      for (const match of matches) {
        if (match[1]) {
          recommendations.push(match[1].trim());
        }
      }
    });

    return recommendations.slice(0, 5); // Limit to 5 recommendations
  },
};

export default TaskReflectionAgentService;
